/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

/**
 * @fileoverview Storage validation utilities for encrypted key synchronization
 * @description Provides functions to validate storage integrity by maintaining
 * encrypted versions of sensitive storage keys and validating them before API calls.
 * Handles auto-creation of encrypted keys on initialization and auto-encryption
 * when setting storage keys.
 *
 * @module StorageUtils
 * @author Autire Technologies, LLC
 * @since 1.0.0
 * @version 1.0.0
 */

import { encrypt, decrypt } from './encryption';

/**
 * Validate that plain text storage values match their decrypted encrypted counterparts
 *
 * @description Validates three storage key pairs:
 * - localStorage.payload vs localStorage.qZ7a
 * - sessionStorage.cpaId vs sessionStorage.L9mX
 * - sessionStorage.companyPlanId vs sessionStorage.t3R
 *
 * Only validates when both plain text and encrypted versions exist.
 * Skips validation if either key is missing.
 *
 * @function validateStorageKeys
 * @returns {Object} Validation result object
 * @returns {boolean} returns.valid - True if all validations pass, false otherwise
 * @returns {string} [returns.failedStorage] - 'local' or 'session' indicating which storage failed
 *
 * @example
 * const result = validateStorageKeys();
 * if (!result.valid) {
 *   console.log(`Validation failed for ${result.failedStorage}Storage`);
 * }
 *
 * @since 1.0.0
 */
export const validateStorageKeys = () => {
  try {
    // Validate localStorage.payload vs localStorage.qZ7a
    const payload = localStorage.getItem('payload');
    const encryptedPayload = localStorage.getItem('qZ7a');

    if (payload && encryptedPayload) {
      const decryptedPayload = decrypt(encryptedPayload);
      if (payload !== decryptedPayload) {
        return { valid: false, failedStorage: 'local' };
      }
    }

    // Validate sessionStorage.cpaId vs sessionStorage.L9mX
    const cpaId = sessionStorage.getItem('cpaId');
    const encryptedCpaId = sessionStorage.getItem('L9mX');

    if (cpaId && encryptedCpaId) {
      const decryptedCpaId = decrypt(encryptedCpaId);
      if (cpaId !== decryptedCpaId) {
        return { valid: false, failedStorage: 'session' };
      }
    }

    // Validate sessionStorage.companyPlanId vs sessionStorage.t3R
    const companyPlanId = sessionStorage.getItem('companyPlanId');
    const encryptedCompanyPlanId = sessionStorage.getItem('t3R');

    if (companyPlanId && encryptedCompanyPlanId) {
      const decryptedCompanyPlanId = decrypt(encryptedCompanyPlanId);
      if (companyPlanId !== decryptedCompanyPlanId) {
        return { valid: false, failedStorage: 'session' };
      }
    }

    return { valid: true };
  } catch (error) {
    return { valid: false, failedStorage: 'session' };
  }
};

/**
 * Initialize storage keys by ensuring encrypted versions exist
 *
 * @description Called when the app loads to ensure data consistency.
 * Checks if encrypted keys exist, and if plain text key exists but
 * encrypted version is missing, creates the encrypted version.
 *
 * Initializes three key pairs:
 * - localStorage.payload → localStorage.qZ7a
 * - sessionStorage.cpaId → sessionStorage.L9mX
 * - sessionStorage.companyPlanId → sessionStorage.t3R
 *
 * @function initializeStorageKeys
 * @returns {void}
 *
 * @example
 * // Call on app initialization
 * initializeStorageKeys();
 *
 * @since 1.0.0
 */
export const initializeStorageKeys = () => {
  // Check and sync localStorage.payload -> localStorage.qZ7a
  const payload = localStorage.getItem('payload');
  const encryptedPayload = localStorage.getItem('qZ7a');

  if (payload && !encryptedPayload) {
    const encrypted = encrypt(payload);
    localStorage.setItem('qZ7a', encrypted.r);
  }

  // Check and sync sessionStorage.cpaId -> sessionStorage.L9mX
  const cpaId = sessionStorage.getItem('cpaId');
  const encryptedCpaId = sessionStorage.getItem('L9mX');

  if (cpaId && !encryptedCpaId) {
    const encrypted = encrypt(cpaId);
    sessionStorage.setItem('L9mX', encrypted.r);
  }

  // Check and sync sessionStorage.companyPlanId -> sessionStorage.t3R
  const companyPlanId = sessionStorage.getItem('companyPlanId');
  const encryptedCompanyPlanId = sessionStorage.getItem('t3R');

  if (companyPlanId && !encryptedCompanyPlanId) {
    const encrypted = encrypt(companyPlanId);
    sessionStorage.setItem('t3R', encrypted.r);
  }
};

/**
 * Set payload in localStorage and automatically set encrypted version
 *
 * @description Sets both the plain text payload and its encrypted version (qZ7a).
 * Use this instead of directly calling localStorage.setItem('payload', value).
 *
 * @function setPayload
 * @param {string} value - The payload value to store
 * @returns {void}
 *
 * @example
 * // Instead of: localStorage.setItem('payload', JSON.stringify(data));
 * setPayload(JSON.stringify(data));
 *
 * @since 1.0.0
 */
export const setPayload = (value) => {
  localStorage.setItem('payload', value);
  const encrypted = encrypt(value);
  localStorage.setItem('qZ7a', encrypted.r);
};

/**
 * Set cpaId in sessionStorage and automatically set encrypted version
 *
 * @description Sets both the plain text cpaId and its encrypted version (L9mX).
 * Use this instead of directly calling sessionStorage.setItem('cpaId', value).
 *
 * @function setCpaId
 * @param {string} value - The cpaId value to store
 * @returns {void}
 *
 * @example
 * // Instead of: sessionStorage.setItem('cpaId', cpaId);
 * setCpaId(cpaId);
 *
 * @since 1.0.0
 */
export const setCpaId = (value) => {
  sessionStorage.setItem('cpaId', value);
  const encrypted = encrypt(value);
  sessionStorage.setItem('L9mX', encrypted.r);
};

/**
 * Set companyPlanId in sessionStorage and automatically set encrypted version
 *
 * @description Sets both the plain text companyPlanId and its encrypted version (t3R).
 * Use this instead of directly calling sessionStorage.setItem('companyPlanId', value).
 *
 * @function setCompanyPlanId
 * @param {string} value - The companyPlanId value to store
 * @returns {void}
 *
 * @example
 * // Instead of: sessionStorage.setItem('companyPlanId', planId);
 * setCompanyPlanId(planId);
 *
 * @since 1.0.0
 */
export const setCompanyPlanId = (value) => {
  sessionStorage.setItem('companyPlanId', value);
  const encrypted = encrypt(value);
  sessionStorage.setItem('t3R', encrypted.r);
};
